import { createApp } from "vue";
import { createPinia } from "pinia";
import { useTitle } from "@vueuse/core";
import App from "./App.vue";
import "@eudonet/opal/styles"; // OPAL component styles
import "@eudonet/opal/styles/design-tokens.css"; // OPAL design tokens
import "./shared/styles/main.css";
import "remixicon/fonts/remixicon.css";
import "flag-icons/css/flag-icons.min.css";
import "flowbite";
import keycloakPlugin, { authState, configureKeycloakForHost } from "./plugins/keycloak";
import router from "./router";
import i18n from "./plugins/i18n";
import { logger } from "./infrastructure/services/LoggerService";
import { configService } from "./infrastructure/services/ConfigService";
import { keycloakAuthService } from "./infrastructure/adapters/KeycloakAuthService";
import { moduleRegistry } from "./router/moduleRegistry";
import { resolveDocumentTitle } from "./shared/utils/documentTitle";
import { eventBus } from "./shared/composables/useEventBus";
import { loadWorkspaceCountryOnAuthentication } from "./shared/composables/useWorkspaceCountry";
import { SHELL_CONTRACTS_KEY } from "./contracts/ShellContracts";
// Import modules
import headerModule from "./modules/header";
import sidebarModule from "./modules/sidebar";
import homeModule from "./modules/home";
import assistantModule from "./modules/assistant";
import settingsModule from "./modules/settings";
import templatingModule from "./modules/templating";
import contactModule from "./modules/contact";
import organisationModule from "./modules/organisation";
import workspaceModule from "./modules/workspace";
import consentModule from "./modules/consent";
import campaignsModule from "./modules/communications";
import financeModule from "./modules/finance";
import projectsModule from "./modules/projects";
import collecteModule from "./modules/collecte";
import documentModule from "./modules/document";
import receptionModule from "./modules/reception";
import schedulingModule from "./modules/scheduling";
import teamModule from "./modules/team";
import complianceModule from "./modules/compliance";
import accessManagerModule from "./modules/access-manager";
import valueListsModule from "./modules/value-lists";
async function initializeApp() {
    await configService.initialize();
    await configureKeycloakForHost();
    const app = createApp(App);
    const pinia = createPinia();
    app.use(pinia);
    app.use(i18n);
    // Initialize Shell Contracts (router is needed for contracts)
    const shellContracts = {
        auth: keycloakAuthService,
        logger,
        router,
        app,
        i18n,
        eventBus,
    };
    // Initialize Module Registry BEFORE using router
    moduleRegistry.initialize(shellContracts);
    // Register all modules BEFORE using router
    moduleRegistry.registerModule(headerModule);
    //moduleRegistry.registerModule(footerModule);
    moduleRegistry.registerModule(sidebarModule);
    moduleRegistry.registerModule(homeModule);
    // OP-6663: the assistant page embeds Orbital — when the runtime flag is off
    // (release, production overlays) the module is not registered at all, so
    // /assistant falls through to the NotFound catch-all.
    if (configService.isOrbitalEnabled) {
        moduleRegistry.registerModule(assistantModule);
    }
    moduleRegistry.registerModule(settingsModule);
    moduleRegistry.registerModule(templatingModule);
    moduleRegistry.registerModule(contactModule);
    moduleRegistry.registerModule(organisationModule);
    moduleRegistry.registerModule(workspaceModule);
    moduleRegistry.registerModule(consentModule);
    moduleRegistry.registerModule(campaignsModule);
    moduleRegistry.registerModule(financeModule);
    moduleRegistry.registerModule(projectsModule);
    moduleRegistry.registerModule(collecteModule);
    moduleRegistry.registerModule(documentModule);
    moduleRegistry.registerModule(receptionModule);
    moduleRegistry.registerModule(schedulingModule);
    moduleRegistry.registerModule(teamModule);
    moduleRegistry.registerModule(complianceModule);
    moduleRegistry.registerModule(accessManagerModule);
    moduleRegistry.registerModule(valueListsModule);
    // Add module routes BEFORE app.use(router) to ensure routes are available on first navigation
    const moduleRoutes = moduleRegistry.getModuleRoutes();
    moduleRoutes.forEach((route) => {
        router.addRoute(route);
    });
    // Now use router with all routes already registered
    app.use(router);
    // Keeps `document.title` on the page the user is actually on — the value the browser tab, the
    // bookmark and a rich clipboard paste of the URL all show. Reading `currentRoute` tracks
    // navigations and vue-i18n's `t()` tracks the active locale, so one getter covers both a route
    // change and a profile-driven language switch (`setLocale` in @/plugins/i18n), and `useTitle`
    // writes only when the resolved string changes. Both dependencies are pinned by tests in
    // `@/shared/utils/documentTitle.spec`.
    //
    // Wired here rather than in `@/router` on purpose: `useTitle` evaluates its getter immediately,
    // so at router-module scope it dereferenced `currentRoute` during import — which broke the specs
    // that stub `vue-router` with inert `createRouter: () => ({ beforeEach, afterEach })` factories
    // (DocumentListView, WorkspaceSettings), because the route table is instantiated by a transitive
    // import there. The app entry point is the first place a real router is guaranteed to exist.
    useTitle(() => resolveDocumentTitle(router.currentRoute.value.matched, (key) => i18n.global.t(key)));
    app.use(keycloakPlugin);
    // CONSTRAINT: keep the deferred (default `pre`) flush of this watcher. keycloak.ts sets
    // `isAuthenticated` before `getAuthBootstrap().start()`, and the api interceptor's
    // `awaitInitial()` gates nothing until `start()` has run: a `sync` flush, or a direct
    // `loadWorkspaceCountry()` call here, can read before the bootstrap and 403 on a first login.
    loadWorkspaceCountryOnAuthentication(() => authState.isAuthenticated);
    // Provide Shell Contracts to the app (for modules to inject)
    app.provide(SHELL_CONTRACTS_KEY, shellContracts);
    app.mount("#app");
}
initializeApp();
const handleKeycloakUrlCleanup = () => {
    const hash = window.location.hash;
    const pathname = window.location.pathname;
    if (pathname.startsWith("/error")) {
        logger.debug(`On error page (${pathname}), cleaning up URL`);
        if (hash) {
            window.history.replaceState({}, document.title, pathname);
        }
        return;
    }
    if (hash.includes("error=") && !hash.includes("error=login_required")) {
        logger.debug("Keycloak error detected in URL, cleaning up parameters");
        window.history.replaceState({}, document.title, pathname);
    }
};
handleKeycloakUrlCleanup();
setTimeout(handleKeycloakUrlCleanup, 300);
